"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLayoutEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check, ChevronRight, Layers, Shield, X } from "lucide-react";
import { api } from "@/lib/axios";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

type RoleRow = { id: number; name: string; description?: string | null; is_system?: number };
type PermRow = { id: number; module: string; action: string; description?: string | null };

function apiErr(e: unknown): string {
  const ax = e as { response?: { data?: { error?: string } } };
  return ax.response?.data?.error ?? (e instanceof Error ? e.message : "Request failed");
}

/** Binary grant — same visual language as Settings → branch mode pill. */
function PermToggle({
  checked,
  disabled,
  onCheckedChange,
  ariaLabel,
}: {
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (next: boolean) => void;
  ariaLabel?: string;
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        "inline-flex shrink-0 rounded-full border border-border/80 bg-muted/45 p-0.5 shadow-inner dark:bg-muted/25",
        "ring-offset-background",
      )}
    >
      <button
        type="button"
        role="radio"
        aria-checked={!checked}
        disabled={disabled}
        onClick={() => {
          if (checked) {
            onCheckedChange(false);
          }
        }}
        className={cn(
          "flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide transition-all duration-200 outline-none",
          "focus-visible:ring-2 focus-visible:ring-violet-500/45 focus-visible:ring-offset-1",
          "disabled:pointer-events-none disabled:opacity-40",
          !checked
            ? "bg-gradient-to-br from-slate-700 to-slate-900 text-white shadow-sm dark:from-slate-600 dark:to-slate-800"
            : "text-muted-foreground hover:bg-background/70 hover:text-foreground",
        )}
      >
        <X className="size-3 shrink-0 opacity-90" strokeWidth={2.5} />
        Off
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => {
          if (!checked) {
            onCheckedChange(true);
          }
        }}
        className={cn(
          "flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide transition-all duration-200 outline-none",
          "focus-visible:ring-2 focus-visible:ring-violet-500/45 focus-visible:ring-offset-1",
          "disabled:pointer-events-none disabled:opacity-40",
          checked
            ? "bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-sm"
            : "text-muted-foreground hover:bg-background/70 hover:text-foreground",
        )}
      >
        <Check className="size-3 shrink-0 opacity-90" strokeWidth={2.5} />
        On
      </button>
    </div>
  );
}

/** Module-level grant all / clear — Off | All pill + amber ring when partially granted. */
function ModuleGrantToggle({
  state,
  disabled,
  onGrantAll,
}: {
  state: "all" | "some" | "none";
  disabled: boolean;
  onGrantAll: (grant: boolean) => void;
}) {
  return (
    <div
      className={cn(
        "inline-flex rounded-full border border-border/80 bg-muted/45 p-0.5 shadow-inner dark:bg-muted/25",
        state === "some" && "ring-2 ring-amber-400/45 ring-offset-2 ring-offset-background dark:ring-amber-500/35",
      )}
      role="group"
      aria-label="Module permissions"
    >
      <button
        type="button"
        role="radio"
        aria-checked={state === "none"}
        disabled={disabled}
        onClick={() => {
          if (state !== "none") {
            onGrantAll(false);
          }
        }}
        className={cn(
          "rounded-full px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide transition-all duration-200 outline-none",
          "focus-visible:ring-2 focus-visible:ring-violet-500/45 focus-visible:ring-offset-1",
          "disabled:pointer-events-none disabled:opacity-40",
          state === "none"
            ? "bg-gradient-to-br from-slate-700 to-slate-900 text-white shadow-sm dark:from-slate-600 dark:to-slate-800"
            : "text-muted-foreground hover:bg-background/70 hover:text-foreground",
        )}
      >
        Off
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={state === "all"}
        disabled={disabled}
        onClick={() => {
          if (state !== "all") {
            onGrantAll(true);
          }
        }}
        className={cn(
          "rounded-full px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wide transition-all duration-200 outline-none",
          "focus-visible:ring-2 focus-visible:ring-violet-500/45 focus-visible:ring-offset-1",
          "disabled:pointer-events-none disabled:opacity-40",
          state === "all"
            ? "bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-sm"
            : "text-muted-foreground hover:bg-background/70 hover:text-foreground",
        )}
      >
        All
      </button>
    </div>
  );
}

function setsEqual(a: Set<number>, b: Set<number>): boolean {
  if (a.size !== b.size) {
    return false;
  }
  for (const x of a) {
    if (!b.has(x)) {
      return false;
    }
  }

  return true;
}

export default function AdminRolesPage() {
  const qc = useQueryClient();
  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ["admin", "roles"],
    queryFn: async () => {
      const { data: d } = await api.get<{ roles: RoleRow[] }>("/api/roles");
      return (d.roles ?? []).map((r) => ({
        ...r,
        id: Number((r as unknown as { id: string | number }).id),
      }));
    },
  });

  const { data: permData, isLoading: permsLoading } = useQuery({
    queryKey: ["admin", "permissions"],
    queryFn: async () => {
      const { data: d } = await api.get<{ permissions_by_module: Record<string, PermRow[]> }>(
        "/api/permissions",
      );
      const raw = d.permissions_by_module ?? {};
      const out: Record<string, PermRow[]> = {};
      for (const mod of Object.keys(raw)) {
        out[mod] = (raw[mod] ?? []).map((row) => ({
          ...row,
          id: Number((row as { id: string | number }).id),
          module: String(row.module),
          action: String(row.action),
        }));
      }

      return out;
    },
  });

  const [roleId, setRoleId] = useState<number | "">("");
  const roleMeta = useMemo(() => {
    if (typeof roleId !== "number") {
      return null;
    }
    return (rolesData ?? []).find((r) => r.id === roleId) ?? null;
  }, [roleId, rolesData]);

  const assignedQ = useQuery({
    queryKey: ["admin", "role-perms", roleId],
    enabled: typeof roleId === "number" && roleId > 0,
    queryFn: async () => {
      const { data: d } = await api.get<{ permission_ids: number[] }>(`/api/roles/${roleId}/permissions`);
      const ids = d.permission_ids ?? [];
      return new Set(ids.map((x) => Number(x)));
    },
  });
  const assigned = assignedQ.data;

  const [checked, setChecked] = useState<Set<number>>(new Set());

  const assignedSignature = useMemo(() => {
    if (!assigned || assigned.size === 0) {
      return "";
    }

    return [...assigned].sort((a, b) => a - b).join(",");
  }, [assigned]);

  useLayoutEffect(() => {
    if (typeof roleId !== "number" || roleId <= 0) {
      setChecked(new Set());

      return;
    }
    if (!assigned) {
      setChecked(new Set());

      return;
    }
    setChecked(new Set(assigned));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `assigned` matches `assignedSignature` in this render; omitting avoids refetch Set() wiping toggles
  }, [roleId, assignedSignature]);

  const modules = useMemo(() => {
    if (!permData) {
      return [] as string[];
    }

    return Object.keys(permData)
      .filter((k) => k !== "companies")
      .sort();
  }, [permData]);

  const flatPerms = useMemo(() => {
    const out: PermRow[] = [];
    for (const m of modules) {
      out.push(...(permData?.[m] ?? []));
    }

    return out;
  }, [permData, modules]);

  const isDirty =
    typeof roleId === "number" &&
    roleId > 0 &&
    assigned !== undefined &&
    !setsEqual(checked, assigned);

  const flatCount = useMemo(() => {
    if (!permData) {
      return 0;
    }
    let n = 0;
    for (const m of modules) {
      n += (permData[m] ?? []).length;
    }

    return n;
  }, [permData, modules]);

  const grantedVisible = useMemo(() => {
    let n = 0;
    for (const p of flatPerms) {
      if (checked.has(Number(p.id))) {
        n++;
      }
    }
    return n;
  }, [flatPerms, checked]);
  const permsReady = typeof roleId === "number" && roleId > 0 && assigned !== undefined;

  const save = useMutation({
    mutationFn: async () => {
      if (typeof roleId !== "number") {
        throw new Error("Select a role");
      }
      await api.put(`/api/roles/${roleId}/permissions`, {
        permission_ids: Array.from(new Set(Array.from(checked))),
      });
    },
    onSuccess: async () => {
      toast.success("Permissions saved");
      await qc.invalidateQueries({ queryKey: ["admin", "role-perms", roleId] });
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const switchDisabled = save.isPending || !permsReady;

  function toggleModule(module: string, on: boolean) {
    const rows = permData?.[module] ?? [];
    setChecked((prev) => {
      const next = new Set(prev);
      for (const p of rows) {
        const pid = Number(p.id);
        if (on) {
          next.add(pid);
        } else {
          next.delete(pid);
        }
      }

      return next;
    });
  }

  function moduleState(module: string): "all" | "some" | "none" {
    const rows = permData?.[module] ?? [];
    if (rows.length === 0) {
      return "none";
    }
    let on = 0;
    for (const p of rows) {
      if (checked.has(Number(p.id))) {
        on++;
      }
    }
    if (on === 0) {
      return "none";
    }
    if (on === rows.length) {
      return "all";
    }

    return "some";
  }

  if (rolesLoading || permsLoading) {
    return (
      <div className="min-h-full space-y-6 bg-background p-6">
        <div className="h-32 animate-pulse rounded-2xl bg-muted/60" />
        <div className="h-64 animate-pulse rounded-xl bg-muted/50" />
      </div>
    );
  }

  return (
    <div className="min-h-full space-y-8 bg-background p-6">
      <section className="relative overflow-hidden rounded-2xl border border-border bg-card p-8 shadow-sm">
        <div
          aria-hidden
          className="pointer-events-none absolute -left-20 top-0 size-64 rounded-full bg-primary/10 blur-3xl"
        />
        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary shadow-inner">
              <Shield className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl">Roles & permissions</h1>
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                Pick a role, toggle capabilities by module, then save. Users inherit the permissions of their assigned
                role on next session.
              </p>
            </div>
          </div>
          {typeof roleId === "number" && roleId > 0 ? (
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="secondary" className="tabular-nums">
                {grantedVisible} / {flatCount} permissions
              </Badge>
              {isDirty ? (
                <Badge variant="outline" className="border-amber-500/50 text-amber-700 dark:text-amber-400">
                  Unsaved changes
                </Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground">
                  <Check className="mr-1 size-3" />
                  In sync
                </Badge>
              )}
            </div>
          ) : null}
        </div>
      </section>

      <Card className="border-border shadow-sm">
        <CardHeader className="border-b border-border bg-muted/30">
          <CardTitle className="text-lg">Role</CardTitle>
          <CardDescription>System roles define what each persona can open in the app.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-6 pt-6 md:flex-row md:flex-wrap md:items-end">
          <div className="grid min-w-[240px] flex-1 gap-2">
            <Label htmlFor="role-pick">Select role</Label>
            <Select
              value={roleId === "" ? null : roleId}
              onValueChange={(v) => setRoleId(v == null ? "" : Number(v))}
            >
              <SelectTrigger id="role-pick" className="h-10 w-full min-w-[12rem] md:max-w-sm">
                <SelectValue placeholder="Choose role">
                  {(value: unknown) => {
                    const id = typeof value === "number" ? value : Number(value);
                    if (!Number.isFinite(id) || id <= 0) {
                      return null;
                    }
                    return (rolesData ?? []).find((r) => r.id === id)?.name ?? `Role #${id}`;
                  }}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {(rolesData ?? []).map((r) => (
                  <SelectItem key={r.id} value={r.id} label={r.name}>
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {assignedQ.isError ? (
              <p className="text-sm text-destructive">Could not load permissions for this role.</p>
            ) : null}
          </div>
          {roleMeta?.description ? (
            <p className="text-sm text-muted-foreground md:max-w-md md:pb-2">{roleMeta.description}</p>
          ) : null}
          <div className="flex flex-wrap gap-2 md:ml-auto">
            <Button
              type="button"
              variant="secondary"
              disabled={typeof roleId !== "number" || !permData || !permsReady}
              onClick={() => {
                if (!permData) {
                  return;
                }
                const all = new Set<number>();
                for (const m of modules) {
                  for (const p of permData[m] ?? []) {
                    all.add(p.id);
                  }
                }
                setChecked(all);
              }}
            >
              Grant all
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={typeof roleId !== "number" || !permsReady}
              onClick={() => {
                setChecked((prev) => {
                  const next = new Set(prev);
                  for (const p of flatPerms) {
                    next.delete(Number(p.id));
                  }
                  return next;
                });
              }}
            >
              Clear all
            </Button>
            <Button
              type="button"
              disabled={
                save.isPending ||
                typeof roleId !== "number" ||
                !permsReady ||
                !isDirty
              }
              onClick={() => save.mutate()}
            >
              {save.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {typeof roleId === "number" && roleId > 0 ? (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Layers className="h-4 w-4" />
            Modules
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {modules.map((mod) => {
              const rows = permData?.[mod] ?? [];
              const st = moduleState(mod);

              return (
                <Card key={mod} className="border-border/80 shadow-sm transition-shadow hover:shadow-md">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 border-b border-border/80 bg-muted/20 py-3">
                    <CardTitle className="font-mono text-sm font-semibold tracking-tight text-foreground">
                      {mod}
                    </CardTitle>
                    <div className="flex flex-col items-end gap-1 sm:flex-row sm:items-center sm:gap-2">
                      {st === "some" ? (
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                          Partial
                        </span>
                      ) : null}
                      <ModuleGrantToggle
                        state={st}
                        disabled={switchDisabled || rows.length === 0}
                        onGrantAll={(v) => toggleModule(mod, v)}
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="max-h-[280px] space-y-0 overflow-y-auto p-0">
                    {rows.map((p, i) => (
                      <div
                        key={p.id}
                        className={cn(
                          "flex items-center justify-between gap-3 px-4 py-2.5 transition-colors hover:bg-muted/30",
                          i !== rows.length - 1 && "border-b border-border/60",
                        )}
                      >
                        <div className="min-w-0">
                          <p className="font-mono text-xs font-medium text-foreground">{p.action}</p>
                          {p.description ? (
                            <p className="truncate text-[11px] text-muted-foreground">{p.description}</p>
                          ) : null}
                        </div>
                        <PermToggle
                          ariaLabel={`Grant ${p.module}.${p.action}`}
                          checked={checked.has(Number(p.id))}
                          disabled={switchDisabled}
                          onCheckedChange={(v) => {
                            const pid = Number(p.id);
                            setChecked((prev) => {
                              const n = new Set(prev);
                              if (v) {
                                n.add(pid);
                              } else {
                                n.delete(pid);
                              }

                              return n;
                            });
                          }}
                        />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <details className="rounded-xl border border-border bg-card shadow-sm [&[open]>summary_svg]:rotate-90">
            <summary className="flex cursor-pointer select-none list-none items-center gap-2 border-b border-border bg-muted/30 px-4 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted/50 [&::-webkit-details-marker]:hidden">
              <ChevronRight className="size-4 shrink-0 text-muted-foreground transition-transform" />
              Full permission list (toggle one by one)
            </summary>
            <div className="max-h-[min(70vh,520px)] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-[1] border-b border-border bg-muted/90 backdrop-blur-sm">
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2">Module</th>
                    <th className="px-4 py-2">Action</th>
                    <th className="px-4 py-2 text-right">Grant</th>
                  </tr>
                </thead>
                <tbody>
                  {flatPerms.map((p) => (
                    <tr key={p.id} className="border-b border-border/60 last:border-0 hover:bg-muted/15">
                      <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{p.module}</td>
                      <td className="px-4 py-2.5">
                        <span className="font-mono text-xs font-medium text-foreground">{p.action}</span>
                        {p.description ? (
                          <p className="mt-0.5 max-w-md text-[11px] text-muted-foreground">{p.description}</p>
                        ) : null}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex shrink-0 items-center justify-end">
                          <PermToggle
                            ariaLabel={`Grant ${p.module}.${p.action}`}
                            checked={checked.has(Number(p.id))}
                            disabled={switchDisabled}
                            onCheckedChange={(v) => {
                              const pid = Number(p.id);
                              setChecked((prev) => {
                                const n = new Set(prev);
                                if (v) {
                                  n.add(pid);
                                } else {
                                  n.delete(pid);
                                }

                                return n;
                              });
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </div>
      ) : (
        <Card className="border-dashed border-border bg-muted/20">
          <CardContent className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <Shield className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm font-medium text-foreground">Select a role to edit permissions</p>
            <p className="max-w-sm text-xs text-muted-foreground">
              Choose from the dropdown above. Each module groups related actions you can enable or disable.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
